import { lang, type Lang } from '../i18n';
import { createAnalytics, type IAnalytics } from './analytics';
import { apiBase, currentIdentity } from './identity';

// 計測は analytics.ts に閉じている。ここからは型と実装をそのまま通すだけ。
export type { AnalyticsEvent, IAnalytics } from './analytics';
export { LocalAnalytics } from './analytics';

/**
 * プラットフォーム依存はすべてこのインターフェース越しにする。
 * Web 版とストア版（Capacitor）で実装を差し替え、ゲーム本体は課金の存在を知らない。
 *
 * Phase 0 では最小の localStorage 実装と何もしないスタブだけを置く。
 * 実装を足すのは Phase 2 で、ゲーム側のコードは触らない。
 */

export interface LeaderboardEntry {
  readonly name: string;
  readonly timeSeconds: number;
  readonly treasurePoints: number;
  readonly total: number;
  /** リプレイ本体は別に保存する。ゴーストと検証はこの id 経由で引く。 */
  readonly replayId?: string;
}

export type Board = 'fastest' | 'treasure' | 'overall';

export interface ILeaderboard {
  submit(stageId: string, entry: LeaderboardEntry): Promise<void>;
  top(stageId: string, board: Board, limit: number): Promise<LeaderboardEntry[]>;
}

export interface IStore {
  /** 所有している商品 id。ステージの解放判定に使う。 */
  owned(): Promise<ReadonlySet<string>>;
  purchase(productId: string): Promise<boolean>;
}

export interface IAds {
  /** リワード動画のみ。報酬はソフト通貨と見た目だけで、スコアには影響させない。 */
  showRewarded(): Promise<{ rewarded: boolean }>;
}


/** Phase 0 用。端末内にだけ記録する。 */
export class LocalLeaderboard implements ILeaderboard {
  private key(stageId: string): string {
    return `etw:board:${stageId}`;
  }

  async submit(stageId: string, entry: LeaderboardEntry): Promise<void> {
    const all = this.read(stageId);
    all.push(entry);
    localStorage.setItem(this.key(stageId), JSON.stringify(all.slice(-200)));
  }

  async top(stageId: string, board: Board, limit: number): Promise<LeaderboardEntry[]> {
    const all = this.read(stageId);
    const sorted = [...all].sort((a, b) => {
      if (board === 'fastest') return a.timeSeconds - b.timeSeconds;
      if (board === 'treasure') return b.treasurePoints - a.treasurePoints;
      return b.total - a.total;
    });
    return sorted.slice(0, limit);
  }

  private read(stageId: string): LeaderboardEntry[] {
    try {
      const raw = localStorage.getItem(this.key(stageId));
      return raw ? (JSON.parse(raw) as LeaderboardEntry[]) : [];
    } catch {
      return [];
    }
  }
}

/**
 * サーバー側のランキング。
 * `VITE_API_BASE` が設定されていれば有効になり、無ければ端末内の記録だけで動く。
 * **繋がらなくても遊べることを崩さない。** ランキングは体験の付加であって前提ではない。
 */
export class RemoteLeaderboard implements ILeaderboard {
  private readonly base: string;

  constructor(base: string) {
    this.base = base.replace(/\/$/, '');
  }

  async submit(stageId: string, entry: LeaderboardEntry): Promise<void> {
    // 実際の投稿はリプレイ込みで submitRun を使う。
    // この経路は互換のために残してあるだけで、検証を通らないので記録されない。
    void stageId;
    void entry;
  }

  /**
   * 走行を投稿する。**スコアではなく入力列を送る。**
   * サーバーが再生して出した値だけが記録される。
   */
  async submitRun(payload: {
    boardKey: string;
    playerId: string;
    displayName: string;
    claimedTotal: number;
    replay: unknown;
  }): Promise<{ ok: boolean; reason?: string }> {
    try {
      const res = await fetch(`${this.base}/runs`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.ok) return { ok: true };
      const body = (await res.json().catch(() => ({}))) as { reason?: string };
      return { ok: false, reason: body.reason ?? String(res.status) };
    } catch {
      return { ok: false, reason: 'offline' };
    }
  }

  async top(stageId: string, board: Board, limit: number): Promise<LeaderboardEntry[]> {
    try {
      const res = await fetch(
        `${this.base}/board?key=${encodeURIComponent(stageId)}&board=${board}&limit=${limit}`,
      );
      if (!res.ok) return [];
      const body = (await res.json()) as {
        rows: { display_name: string; time_seconds: number; treasure: number; total: number }[];
      };
      return body.rows.map((r) => ({
        name: r.display_name,
        timeSeconds: r.time_seconds,
        treasurePoints: r.treasure,
        total: r.total,
      }));
    } catch {
      return [];
    }
  }
}

/** 設定されていればサーバー、無ければ端末内。ゲーム側はどちらか知らない。 */
export function createLeaderboard(): ILeaderboard {
  const base = apiBase();
  return base ? new RemoteLeaderboard(base) : new LocalLeaderboard();
}

/**
 * 表示名。プリセットの組み合わせだけを作る。
 * 自由入力を持たせないことで、実名リスクとモデレーションコストを同時に消す。
 */
const NAME_WORDS: Record<Lang, { adjectives: string[]; nouns: string[] }> = {
  ja: {
    adjectives: ['はやい', 'しずかな', 'ふかい', 'あさい', 'あおい', 'まるい', 'ちいさな'],
    nouns: ['ウミガメ', 'イルカ', 'マンタ', 'サンゴ', 'シーグラス', 'まきがい', 'なみ'],
  },
  en: {
    adjectives: ['Swift', 'Quiet', 'Deep', 'Shallow', 'Blue', 'Round', 'Little'],
    nouns: ['Turtle', 'Dolphin', 'Manta', 'Coral', 'Seaglass', 'Shell', 'Wave'],
  },
};

/**
 * 表示名。
 *
 * **言語ごとの語で組み立てる。** 同じ表に日本語名と英語名が並ぶが、
 * それぞれの読み手にとって意味のある名前になる方を取る。
 * サーバーは両方の語彙を受け付ける（`api/src/index.ts`）。
 */
export function makeDisplayName(seed: number): string {
  const { adjectives, nouns } = NAME_WORDS[lang()];
  const a = adjectives[seed % adjectives.length];
  const n = nouns[Math.floor(seed / adjectives.length) % nouns.length];
  return `${a} ${n} ${seed % 100}`;
}

/**
 * 端末ごとの匿名 ID。
 *
 * 実体は `identity.ts` に移した。サーバーが発行した ID を持っていればそれを、
 * 無ければ端末生成のものを返す。**呼び出し側はどちらか知らない。**
 */
export function playerId(): string {
  return currentIdentity().playerId;
}

/** Phase 0 では課金も広告も存在しない。面白さの検証が終わるまで導線を作らない。 */
export const noopStore: IStore = {
  async owned() {
    return new Set<string>();
  },
  async purchase() {
    return false;
  },
};

export const noopAds: IAds = {
  async showRewarded() {
    return { rewarded: false };
  },
};

export const consoleAnalytics: IAnalytics = {
  track(event, props) {
    if (import.meta.env?.DEV) console.info('[analytics]', event, props ?? {});
  },
};

export const analytics: IAnalytics = createAnalytics();
